// App.js
import useChatPreferences from "./firebase/useChatPreferences";
import { signOutWithNotifications } from "./firebase/notificationSession";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Purchases } from "@revenuecat/purchases-js";
import CollabProjectsProvider from "./components/collab-projects/CollabProjectsProvider";
import SessionDataProvider from "./components/session-data/SessionDataProvider";
import ScopedUsersListener from "./components/session-data/ScopedUsersListener";
import { auth } from "./firebase/firebase.utils";
import {
  createOrLoadUserProfile,
  ensurePrivateBirthday,
  getPrivateUserProfile,
  hasCachedPrivateBirthday,
  syncVerifiedUser,
} from "./firebase/auth-profile.utils";
import {
  setCurrentUser,
  signOut as clearReduxUser,
} from "./redux/user/user.actions";
import { logOut } from "./redux/space/space.actions";
import { setLoading } from "./redux/history/history.actions";
import LayoutFront from "./pages/welcome-page/frontpages/LayoutFront";
import SignIn from "./pages/sign-in/sign-in.component.class";
import AccountAccess from "./pages/account-access/AccountAccess";
import VerifyEmail from "./components/auth/verify-email.component";
import CompleteProfile from "./components/auth/complete-profile.component";
import ProtectedRoutes from "./components/protectedRoutes/protectedRoutes.component";
import Space from "./pages/space/space.component.class";
import Home from "./pages/home/home.component.class";
import DockStation from "./pages/dock-station/dock-station.component";
import EnterStation from "./pages/enter-station/enter-station.component";
import Chat from "./pages/chat/chat.component";
import LoadingPage from "./components/retro/loading-page/loading-page.component";
import MobileMenu from "./components/mobile-menu/mobile-menu.component";
import PersonNotifications from "./components/notifications/person-notifications.component";
import TaskDetail from "./components/task-detail/TaskDetail";
import NoteDetail from "./components/note-detail/note-detail";
import FocusApp from "./components/focus/focus.component";
import Paywall from "./components/marketing/Paywall";
import InvoiceBuilder from "./components/billings/invoice-builder.component";
import ExpenseDetail from "./components/billings/expense-detail.component";
import SocialRouteLayer from "./components/socials/pages/SocialRouteLayer";
import PostPage from "./components/socials/pages/PostPage";
import PostCreatePage from "./components/socials/pages/PostCreatePage";
import Profile from "./components/socials/profile/Profile";
import Account from "./components/socials/profile/Account";
import SearchPeople from "./components/socials/profile/SearchPeople";
import Connections from "./components/socials/profile/Connections";
import Invitation from "./components/socials/profile/Invitation";
import NotFoundPage from "./pages/404/notFound.component";
import Invites from "./components/members/invites.component";
import "./app.styles.scss";
const revenueCatBillingKeyPublic = process.env.REACT_APP_RCATBILLING_PUBLIC_KEY;
const ChatLayout = () => (
  <CollabProjectsProvider>
    <SessionDataProvider>
      <ScopedUsersListener />
      <PersonNotifications>
        <Space />
        <Outlet />
        <Chat />
        <FocusApp />
      </PersonNotifications>
    </SessionDataProvider>
  </CollabProjectsProvider>
);
const HomeLayout = ({ isLoading }) => (
  <>
    {isLoading && <LoadingPage />}
    <Home />
    <Outlet />
  </>
);
const HomeIndex = () => (
  <>
    <MobileMenu />
    <Paywall />
  </>
);
const StationLayout = ({ isLoading }) => (
  <>
    {isLoading && <LoadingPage />}
    <DockStation />
    <Outlet />
  </>
);
const destinationAfterAuth = (location) => {
  const from = location.state?.from;
  if (!from?.pathname) return "/";
  if (
    ["/signin", "/verify-email", "/complete-profile"].includes(from.pathname)
  ) {
    return "/";
  }
  return from;
};
const SignInRoute = ({ authReady, firebaseUser, needsBirthday }) => {
  const location = useLocation();
  if (!authReady) return <LoadingPage />;
  if (!firebaseUser) return <SignIn />;
  if (!firebaseUser.emailVerified) {
    return <Navigate to="/verify-email" state={location.state} replace />;
  }
  if (needsBirthday) {
    return <Navigate to="/complete-profile" state={location.state} replace />;
  }
  return <Navigate to={destinationAfterAuth(location)} replace />;
};
const VerifyEmailRoute = ({
  authReady,
  firebaseUser,
  needsBirthday,
  onVerified,
}) => {
  const location = useLocation();
  if (!authReady) return <LoadingPage />;
  if (!firebaseUser) {
    return <Navigate to="/signin" state={location.state} replace />;
  }
  if (firebaseUser.emailVerified) {
    if (needsBirthday) {
      return <Navigate to="/complete-profile" state={location.state} replace />;
    }
    return <Navigate to={destinationAfterAuth(location)} replace />;
  }
  return <VerifyEmail onVerified={onVerified} />;
};
const CompleteProfileRoute = ({
  authReady,
  firebaseUser,
  needsBirthday,
  onCompleted,
}) => {
  const location = useLocation();
  if (!authReady) return <LoadingPage />;
  if (!firebaseUser) {
    return <Navigate to="/signin" state={location.state} replace />;
  }
  if (!firebaseUser.emailVerified) {
    return <Navigate to="/verify-email" state={location.state} replace />;
  }
  if (!needsBirthday) {
    return <Navigate to={destinationAfterAuth(location)} replace />;
  }
  return <CompleteProfile onCompleted={onCompleted} />;
};
function App() {
  const isAccountAccess = useLocation().pathname === "/account-access";
  const { currentUser } = useSelector((state) => state.user);
  const isLoading = useSelector((state) => state.history.isLoading);
  const dispatch = useDispatch();
  useChatPreferences();
  const [authReady, setAuthReady] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [needsBirthday, setNeedsBirthday] = useState(null);
  const [authError, setAuthError] = useState("");
  const authRequestId = useRef(0);
  const applyFirebaseUser = useCallback(
    async (incomingUser, { forceTokenRefresh = false } = {}) => {
      const requestId = ++authRequestId.current;
      setAuthReady(false);
      setAuthError("");
      try {
        if (!incomingUser) {
          setFirebaseUser(null);
          setNeedsBirthday(null);
          dispatch(clearReduxUser());
          dispatch(logOut());
          dispatch(setLoading(true));
          return;
        }
        // onAuthStateChanged already supplies Firebase's restored user. A
        // network reload on every launch adds latency and is only useful when
        // the user explicitly asks us to re-check a verification change.
        const refreshedUser = incomingUser;
        if (!refreshedUser) {
          setFirebaseUser(null);
          setNeedsBirthday(null);
          dispatch(clearReduxUser());
          dispatch(logOut());
          dispatch(setLoading(true));
          return;
        }
        setFirebaseUser(refreshedUser);
        if (!refreshedUser.emailVerified) {
          setNeedsBirthday(null);
          dispatch(clearReduxUser());
          return;
        }
        const birthdayCached = hasCachedPrivateBirthday(refreshedUser.uid);
        const [loadedProfile, privateProfile] = await Promise.all([
          createOrLoadUserProfile(refreshedUser, refreshedUser.displayName),
          birthdayCached
            ? Promise.resolve(null)
            : getPrivateUserProfile(refreshedUser.uid),
        ]);
        const profile = await syncVerifiedUser(refreshedUser, loadedProfile, {
          forceTokenRefresh,
        });
        const hasBirthday = birthdayCached
          ? true
          : await ensurePrivateBirthday(
              refreshedUser.uid,
              profile,
              privateProfile,
            );
        if (requestId !== authRequestId.current) return;
        dispatch(setCurrentUser(profile));
        setNeedsBirthday(!hasBirthday);
      } catch (error) {
        if (requestId !== authRequestId.current) return;
        console.error("Authentication bootstrap failed:", error);
        setAuthError(
          "We could not finish loading your account. Check your connection and try again.",
        );
      } finally {
        if (requestId === authRequestId.current) setAuthReady(true);
      }
    },
    [dispatch],
  );
  const refreshAuthenticatedUser = useCallback(
    () => applyFirebaseUser(auth.currentUser),
    [applyFirebaseUser],
  );
  const handleVerified = useCallback(
    (verifiedUser) =>
      applyFirebaseUser(verifiedUser || auth.currentUser, {
        forceTokenRefresh: true,
      }),
    [applyFirebaseUser],
  );
  const handleProfileCompleted = useCallback(() => {
    // savePrivateDateOfBirth has already committed the private document and
    // cached completion. The public profile is already in Redux, so no second
    // bootstrap read is needed here.
    setNeedsBirthday(false);
    setAuthError("");
  }, []);
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((nextUser) => {
      void applyFirebaseUser(nextUser);
    });
    return () => {
      authRequestId.current += 1;
      unsubscribe();
    };
  }, [applyFirebaseUser]);
  useEffect(() => {
    if (
      !revenueCatBillingKeyPublic ||
      !currentUser?.uid ||
      !firebaseUser?.emailVerified ||
      needsBirthday
    ) {
      return;
    }
    Purchases.configure(revenueCatBillingKeyPublic, currentUser.uid);
  }, [currentUser?.uid, firebaseUser?.emailVerified, needsBirthday]);
  useEffect(() => {
    const selectedTheme =
      localStorage.getItem("selectedTheme") || "paper-theme";
    document.body.setAttribute("data-theme", selectedTheme);
  }, []);
  if (authReady && authError && !isAccountAccess) {
    return (
      <div className="authGate authGate--fullPage">
        <div className="authGate__card">
          <h2>Couldn’t load your account</h2>
          <p>{authError}</p>
          <button type="button" onClick={refreshAuthenticatedUser}>
            Try again
          </button>
          <button type="button" onClick={() => signOutWithNotifications()}>
            Sign out
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="app">
      <Routes>
        <Route path="/account-access" element={<AccountAccess />} />
        <Route element={<LayoutFront />}>
          <Route
            path="/signin"
            element={
              <SignInRoute
                authReady={authReady}
                firebaseUser={firebaseUser}
                needsBirthday={needsBirthday}
              />
            }
          />
          <Route
            path="/verify-email"
            element={
              <VerifyEmailRoute
                authReady={authReady}
                firebaseUser={firebaseUser}
                needsBirthday={needsBirthday}
                onVerified={handleVerified}
              />
            }
          />
          <Route
            path="/complete-profile"
            element={
              <CompleteProfileRoute
                authReady={authReady}
                firebaseUser={firebaseUser}
                needsBirthday={needsBirthday}
                onCompleted={handleProfileCompleted}
              />
            }
          />
        </Route>
        <Route
          element={
            <ProtectedRoutes
              authReady={authReady}
              firebaseUser={firebaseUser}
              isAuth={currentUser}
              needsBirthday={needsBirthday}
            />
          }
        >
          <Route element={<ChatLayout />}>
            <Route path="/" element={<HomeLayout isLoading={isLoading} />}>
              <Route index element={<HomeIndex />} />
              <Route path="addtask" element={<TaskDetail mode="add" />} />
              <Route path="task/:taskId" element={<TaskDetail mode="edit" />} />
              <Route path="addnote" element={<NoteDetail mode="add" />} />
              <Route path="note/:noteId" element={<NoteDetail mode="edit" />} />
              <Route path="invites" element={<Invites />} />
              <Route element={<SocialRouteLayer />}>
                <Route path="profile/:uid" element={<Profile />} />
                <Route
                  path="profile/:uid/connections"
                  element={<Connections />}
                />
                <Route path="people" element={<SearchPeople />} />
                <Route path="account" element={<Account />} />
                <Route path="invitation/:inviteId" element={<Invitation />} />
                <Route path="post/new" element={<PostCreatePage />} />
                <Route path="post/:postId" element={<PostPage />} />
              </Route>
            </Route>
            <Route
              path="/s/:wspaceid"
              element={<StationLayout isLoading={isLoading} />}
            >
              <Route index element={<></>} />
              <Route path="addtask" element={<TaskDetail mode="add" />} />
              <Route path="task/:taskId" element={<TaskDetail mode="edit" />} />
              <Route path="addnote" element={<NoteDetail mode="add" />} />
              <Route
                path="billings/invoices/new"
                element={<InvoiceBuilder mode="create" />}
              />
              <Route
                path="billings/invoices/:invoiceId"
                element={<InvoiceBuilder mode="edit" />}
              />
              <Route
                path="billings/expenses/new"
                element={<ExpenseDetail mode="create" />}
              />
              <Route
                path="billings/expenses/:expenseId"
                element={<ExpenseDetail mode="edit" />}
              />
            </Route>
            <Route
              path="/s/:wspaceid/e/:projectid/*"
              element={
                <>
                  {isLoading && <LoadingPage />}
                  <EnterStation isAuth={currentUser} />
                </>
              }
            />
            <Route
              path="/s/sharedwithme/e/:projectid/*"
              element={
                <>
                  {isLoading && <LoadingPage />}
                  <EnterStation isAuth={currentUser} />
                </>
              }
            />
          </Route>
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  );
}
export default App;
